
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function OfficeElectricalFitOutsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Office Electrical Fit-Outs',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US'
      }
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California'
    },
    description:
      'Professional office electrical fit-out services in Fresno CA for tenant improvements, office renovations, and workspace electrical installations',
    url: `${siteUrl}/services/commercial-specialist/office-electrical-fit-outs`
  };

  const features = [
    {
      icon: 'ri-layout-grid-line',
      title: 'Open Office Electrical',
      description:
        'Power and data distribution for modern open-plan office layouts'
    },
    {
      icon: 'ri-door-line',
      title: 'Private Office Wiring',
      description: 'Dedicated circuits and lighting for individual office spaces'
    },
    {
      icon: 'ri-group-line',
      title: 'Conference Room Systems',
      description:
        'Electrical infrastructure for AV equipment and presentation systems'
    },
    {
      icon: 'ri-computer-line',
      title: 'Workstation Power',
      description:
        'Convenient outlets and USB charging for employee workstations'
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Office Lighting Design',
      description:
        'Energy-efficient LED lighting with dimming and controls'
    },
    {
      icon: 'ri-wifi-line',
      title: 'Network Infrastructure',
      description:
        'Electrical support for IT systems, servers, and communication equipment'
    }
  ];

  return (
    <>
      <SEOHead
        title="Office Electrical Fit-Outs Fresno | Tenant Improvement Electrician CA"
        description="Expert office electrical fit-out services in Fresno CA. Tenant improvements, office renovations, workstation power, lighting, and IT infrastructure. Licensed commercial electricians."
        canonical="/services/commercial-specialist/office-electrical-fit-outs"
        keywords="office electrical fit-out Fresno, tenant improvement electrician, office renovation electrical, workspace electrical"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Office Electrical Fit-Outs — Fresno, CA"
        heroSubtitle="Complete electrical solutions for office tenant improvements and workspace renovations in Fresno"
        heroImage="https://public.readdy.ai/ai/img_res/e6339d72eac471dc8d9ca703abd607ad.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Commercial%20electrician%20installing%20floor%20power%20outlets%20and%20data%20connections%20in%20modern%20office%20workspace%20with%20cable%20management%20and%20organized%20wiring%20infrastructure%20professional%20environment&width=700&height=600&seq=office-fitout-fresno-001&orientation=portrait"
        fresnoContent="Transforming Fresno office spaces with professional electrical fit-out services. From downtown high-rises to suburban office parks in North Fresno and business centers throughout the area, we provide complete electrical installations for tenant improvements and office renovations. Our licensed electricians design and install power distribution, lighting systems, and IT infrastructure that support modern workplace productivity. We understand the unique needs of Fresno businesses and deliver code-compliant electrical fit-outs on schedule and within budget, minimizing disruption to your operations."
        features={features}
        faqs={[
          {
            question:
              'What is included in an office electrical fit-out in Fresno?',
            answer:
              'Our office electrical fit-out services in Fresno CA include complete electrical design and installation for tenant improvements and office renovations. This includes power distribution panels and sub-panels, workstation outlets and USB charging, overhead and task lighting with controls, conference room AV electrical, break room and kitchen circuits, server room and IT infrastructure electrical, emergency lighting and exit signs, HVAC electrical connections, and all required permits and inspections. We work closely with architects, designers, and general contractors to deliver turnkey electrical solutions for Fresno office spaces.'
          },
          {
            question: 'How long does an office electrical fit-out take in Fresno?',
            answer:
              'Office electrical fit-out timelines in Fresno vary based on space size and complexity. Small office suites (1,000-2,000 sq ft) typically take 1-2 weeks for electrical work. Medium offices (2,000-5,000 sq ft) average 2-4 weeks. Large office spaces or multi-floor fit-outs may require 4-8 weeks for complete electrical installation. We coordinate with general contractors and other trades to keep projects on schedule. Many Fresno office fit-outs are completed during evenings and weekends to minimize disruption if the space is partially occupied. We provide detailed schedules during the planning phase.'
          },
          {
            question:
              'Can you work around our business operations in Fresno?',
            answer:
              'Yes, we specialize in occupied office electrical work throughout Fresno. Many tenant improvements are completed while adjacent offices remain operational. We schedule noisy or disruptive work during evenings, weekends, or off-hours. For phased projects, we can complete electrical work in sections to keep portions of your office functional during construction. We use dust barriers, maintain clean work areas, and coordinate closely with your team to minimize impact on business operations. Our goal is to deliver your new office space with minimal disruption to productivity.'
          },
          {
            question:
              'Do you handle permits and inspections for office fit-outs in Fresno?',
            answer:
              'Absolutely. We handle all electrical permitting and inspection coordination for office fit-outs in Fresno CA. We pull permits through the City of Fresno Building Department, ensure all work meets California electrical codes and Title 24 energy requirements, coordinate inspections with city inspectors, address any inspection comments or corrections, and provide final approval documentation for your landlord or property manager. Our licensed C-10 contractors maintain excellent relationships with Fresno building inspectors and ensure smooth project approval.'
          },
          {
            question: 'What does an office electrical fit-out cost in Fresno?',
            answer:
              'Office electrical fit-out costs in Fresno typically range from $8-$15 per square foot for standard installations. A 2,000 sq ft office averages $16,000-$30,000 for complete electrical work. Costs vary based on outlet density, lighting complexity, IT infrastructure requirements, and existing building conditions. High-end offices with extensive lighting controls, AV systems, and specialized equipment may cost $15-$25+ per square foot. We provide detailed written estimates after reviewing plans and conducting site surveys. Many Fresno businesses qualify for PG&E energy rebates when installing energy-efficient lighting and controls.'
          },
          {
            question: 'Can you design the electrical system for our Fresno office?',
            answer:
              'Yes, we provide complete electrical design services for office fit-outs in Fresno CA. Our licensed electricians work with your architect, interior designer, and space planner to create electrical layouts that support your workflow and technology needs. We design power distribution for workstations, plan lighting layouts for productivity and energy efficiency, specify electrical infrastructure for IT and communication systems, ensure adequate capacity for current and future needs, and create systems that meet California Title 24 energy codes. We deliver practical, cost-effective electrical designs tailored to your business operations.'
          }
        ]}
      />
    </>
  );
}
